import type { SystemStyleObject } from '@chakra-ui/react';

import { IMPERSONATION_PRESETS, type UserOverrides } from '@revelio/auth';
import {
  type Client,
  MetadataKey,
  TrialType,
  type User,
} from '@revelio/data-access';

export type SelectOption<T> = {
  label: string;
  value: T;
};

export type StatusDetails = {
  label: string;
  colorScheme: string;
};

export type MaybeUser = Partial<User> | null | undefined;

export const DEFAULT_TRIAL_EXPIRATION_DAYS = 30;

export const DEFAULT_CUSTOM_OVERRIDES: UserOverrides = {
  isTrialUser: false,
  trialType: undefined,
  expirationDate: undefined,
  tourCompleted: undefined,
  stepsCompleted: undefined,
  tourInterestTalentAcquisition: undefined,
  tourInterestCustomData: undefined,
  live: true,
  clientName: undefined,
  role: undefined,
  tabs: undefined,
  linkupPostings: undefined,
  pipelineTypes: undefined,
  postingSources: undefined,
  reportTypes: undefined,
};

export const getTrialExpirationDate = (days: number) => {
  const expirationDate = new Date();
  expirationDate.setDate(expirationDate.getDate() + days);
  return expirationDate.toISOString();
};

export const resolvePresetName = (overrides: UserOverrides) => {
  const matchedPreset = IMPERSONATION_PRESETS.find((preset) => {
    const presetOverrides = preset.overrides;
    const matchesBaseOverrides =
      presetOverrides.trialType === overrides.trialType &&
      presetOverrides.isTrialUser === overrides.isTrialUser &&
      presetOverrides.live === overrides.live &&
      presetOverrides.tourCompleted === overrides.tourCompleted &&
      presetOverrides.stepsCompleted === overrides.stepsCompleted;

    const hasExtraOverrides =
      overrides.role !== undefined ||
      overrides.clientName !== undefined ||
      overrides.tabs !== undefined ||
      overrides.linkupPostings !== undefined ||
      overrides.tourInterestTalentAcquisition !== undefined ||
      overrides.tourInterestCustomData !== undefined ||
      overrides.pipelineTypes !== undefined ||
      overrides.postingSources !== undefined ||
      overrides.reportTypes !== undefined;

    return matchesBaseOverrides && !hasExtraOverrides;
  });

  return matchedPreset?.name ?? null;
};

export const selectControlStyles = {
  control: (provided: SystemStyleObject) => ({
    ...provided,
    height: 'auto',
    backgroundColor: 'white',
    borderColor: 'gray.300',
    boxShadow: 'sm',
    '&:hover': {
      borderColor: 'gray.400',
    },
  }),
};

export const multiSelectStyles = {
  ...selectControlStyles,
  multiValue: (provided: SystemStyleObject) => ({
    ...provided,
    backgroundColor: 'blue.100',
    borderColor: 'blue.300',
    borderWidth: '1px',
    borderRadius: 'md',
  }),
  multiValueLabel: (provided: SystemStyleObject) => ({
    ...provided,
    color: 'blue.800',
  }),
};

const getUserMetadataValue = (
  user: MaybeUser,
  key: MetadataKey
): string | undefined =>
  user?.metadata?.find((metadata) => metadata?.key === key)?.value ?? undefined;

const getBooleanMetadataValue = (
  user: MaybeUser,
  key: MetadataKey
): boolean | undefined => {
  const value = getUserMetadataValue(user, key);
  if (value === undefined) {
    return undefined;
  }

  return value === 'true';
};

type MaybeClient = Partial<Client> | null | undefined;
const filterDefined = <T>(
  items: Array<T | null | undefined> | null | undefined
): T[] | undefined => {
  if (!items) {
    return undefined;
  }

  return items.filter((item): item is T => item !== null && item !== undefined);
};

export const getTrialTypeFromUser = (
  user: MaybeUser
): TrialType | undefined => {
  if (!user || user.live !== false) {
    return undefined;
  }

  const trialType = getUserMetadataValue(user, MetadataKey.TrialType);
  return (trialType as TrialType) ?? TrialType.ONE_YEAR_DATA_LAG;
};

export const getTrialExpirationFromUser = (
  user: MaybeUser
): string | undefined => {
  if (!user || user.live !== false) {
    return undefined;
  }

  return getUserMetadataValue(user, MetadataKey.ExpirationDate);
};

export const isTrialExpiredForUser = (user: MaybeUser): boolean => {
  const expirationDate = getTrialExpirationFromUser(user);
  if (!expirationDate) {
    return false;
  }

  return new Date(expirationDate) < new Date();
};

export const buildOverridesFromUser = (user: Partial<User>): UserOverrides => ({
  isTrialUser: user.live === false,
  live: user.live ?? undefined,
  trialType: getTrialTypeFromUser(user),
  expirationDate: getTrialExpirationFromUser(user),
  tourCompleted: getBooleanMetadataValue(user, MetadataKey.TourCompleted),
  stepsCompleted: getUserMetadataValue(user, MetadataKey.StepsCompleted),
  clientName: user.client_name ?? undefined,
  role: user.role ?? undefined,
  tabs: user.tabs ?? undefined,
  linkupPostings: user.linkup_postings ?? undefined,
});

export const buildOverridesFromClient = (
  client: MaybeClient
): UserOverrides => ({
  pipelineTypes: filterDefined(
    client?.data_builder_configuration?.pipeline_type
  ),
  postingSources: filterDefined(
    client?.data_builder_configuration?.posting_source
  ),
  reportTypes: filterDefined(client?.reports_configuration?.report_types),
});

// Seed the full set of client-group-level overrides when an admin picks a
// client group. Extends buildOverridesFromClient (datasets/postings/reports)
// with the group's tabs and website-postings so custom overrides reflect it.
// Kept separate so buildOverridesFromClient stays tab-free where it merges on
// top of the real/base user (whose own tabs must win there).
export const buildClientGroupSeedOverrides = (
  client: MaybeClient
): UserOverrides => ({
  ...buildOverridesFromClient(client),
  tabs: filterDefined(client?.tabs),
  linkupPostings: client?.linkup_postings ?? undefined,
});

export const mergeOverridesWithDefaults = (
  overrides: UserOverrides
): UserOverrides => ({
  ...DEFAULT_CUSTOM_OVERRIDES,
  ...overrides,
});

export const getActiveStatusDetails = (
  isActive: boolean | null | undefined
): StatusDetails => {
  if (isActive === true) {
    return { label: 'Active', colorScheme: 'green' };
  }

  if (isActive === false) {
    return { label: 'Inactive', colorScheme: 'red' };
  }

  return { label: 'Active Unknown', colorScheme: 'gray' };
};

export const getTrialStatusDetails = (
  isTrialUser: boolean,
  isTrialExpired: boolean
): StatusDetails => {
  if (!isTrialUser) {
    return { label: 'Live User', colorScheme: 'blue' };
  }

  if (isTrialExpired) {
    return { label: 'Trial Expired', colorScheme: 'red' };
  }

  return { label: 'Trial Active', colorScheme: 'green' };
};
